import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  CheckCircle, Users, Award, Clock, Calendar, Star, ArrowRight,
  BookOpen, Briefcase, TrendingUp, Mail,
  ChevronDown, ChevronUp, Target, Globe, AlertTriangle,
  GraduationCap, UserCheck, BarChart3, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_skanda-admin/artifacts/473fldg8_skanda.png";

// Brand Colors from Logo
const COLORS = {
  primary: '#0044A7',
  secondary: '#25B283',
  accent: '#32CD99',
  dark: '#1C3B78',
  gradient: 'linear-gradient(135deg, #32CD99 0%, #0044A7 100%)',
};

// Icon mapping for dynamic content
const ICON_MAP = {
  Users, Target, UserCheck, TrendingUp, GraduationCap, BarChart3,
  Briefcase, Award, Globe, CheckCircle, BookOpen, Clock, Calendar
};

// Registration Modal Component
function RegistrationModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '+91',
    experience: '',
    currentRole: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: `${formData.countryCode} ${formData.phone}`,
        service: 'SAP SuccessFactors Training',
        message: `Experience: ${formData.experience}, Current Role: ${formData.currentRole}`
      };

      await axios.post(`${BACKEND_URL}/api/contact`, submitData);
      
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'SAP SuccessFactors Registration',
          content_category: 'Training'
        });
      }

      toast.success('Registration successful!');
      onSuccess();
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div 
          className="sticky top-0 p-6 rounded-t-2xl"
          style={{ background: COLORS.gradient }}
        >
          <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-center gap-3 mb-3">
            <img src={LOGO_URL} alt="Skanda IT Solutions" className="h-10 w-auto bg-white rounded p-1" />
          </div>
          <h2 className="text-xl font-bold text-white">Register for SAP SuccessFactors Training</h2>
          <p className="text-white/80 text-sm mt-1">Complete the form to secure your seat</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': COLORS.secondary }}
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:border-transparent"
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:border-transparent"
              placeholder="your.email@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
            <div className="flex gap-2">
              <select
                value={formData.countryCode}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                className="w-24 px-3 py-2.5 border border-slate-300 rounded-lg text-sm"
              >
                <option value="+91">+91</option>
                <option value="+1">+1</option>
                <option value="+44">+44</option>
                <option value="+971">+971</option>
                <option value="+966">+966</option>
                <option value="+65">+65</option>
              </select>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9]/g, '') })}
                className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg"
                placeholder="Enter phone number"
                maxLength={10}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Experience Level *</label>
            <select
              required
              value={formData.experience}
              onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg"
            >
              <option value="">Select experience level</option>
              <option value="fresher">Fresher (0-1 years)</option>
              <option value="junior">Junior (1-3 years)</option>
              <option value="mid">Mid-Level (3-5 years)</option>
              <option value="senior">Senior (5+ years)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Current Role (Optional)</label>
            <input
              type="text"
              value={formData.currentRole}
              onChange={(e) => setFormData({ ...formData, currentRole: e.target.value })}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg"
              placeholder="e.g., HR Executive, IT Professional"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3.5 text-white rounded-lg font-semibold hover:opacity-90 transition-all disabled:opacity-50"
            style={{ background: COLORS.gradient }}
          >
            {isSubmitting ? 'Processing...' : 'Submit Registration'}
          </button>

          <p className="text-xs text-slate-500 text-center">
            By registering, you agree to receive course updates via email and WhatsApp.
          </p>
        </form>
      </div>
    </div>
  );
}

// FAQ Component
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between py-5 text-left hover:text-slate-900 transition-colors"
      >
        <span className="font-medium text-slate-800 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp style={{ color: COLORS.secondary }} className="shrink-0" size={20} />
        ) : (
          <ChevronDown className="text-slate-400 shrink-0" size={20} />
        )}
      </button>
      {isOpen && (
        <div className="pb-5 text-slate-600 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

// Loading skeleton component
function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: COLORS.primary }} />
        <p className="text-slate-600">Loading...</p>
      </div>
    </div>
  );
}

// Main Component
export default function SAPSuccessFactorsLanding() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(0);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/landing-pages/sap-successfactors`);
        setPageData(response.data);
      } catch (err) {
        console.error('Error fetching landing page data:', err);
        setError('Failed to load page content');
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  const handleRegistrationSuccess = () => {
    setIsModalOpen(false);
    navigate('/sap-successfactors/thank-you');
  };

  if (loading) return <LoadingSkeleton />;
  
  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <p className="text-slate-600">{error || 'Page not found'}</p>
          <a href="/" className="mt-4 inline-block text-blue-600 hover:underline">Return to Home</a>
        </div>
      </div>
    );
  }

  // Get icon component from name
  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Users;
  };

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title}</title>
        <meta name="description" content={pageData.meta_description} />
        <link rel="canonical" href="https://www.skandaitsolutions.com/sap-successfactors" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-100 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex justify-between items-center">
              <a href="/">
                <img src={LOGO_URL} alt="Skanda IT Solutions" className="h-10 sm:h-12 w-auto" />
              </a>
              <button
                onClick={() => setIsModalOpen(true)}
                data-testid="header-enroll-btn"
                className="px-5 py-2.5 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-colors"
                style={{ backgroundColor: COLORS.primary }}
              >
                Enroll Now
              </button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 sm:pt-32 sm:pb-24" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Content */}
              <div>
                <div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
                  style={{ backgroundColor: `${COLORS.accent}15`, color: COLORS.dark }}
                >
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: COLORS.accent }}></span>
                  {pageData.hero_badge}
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6" style={{ color: COLORS.dark }}>
                  {pageData.hero_title}
                  <span className="block" style={{ color: COLORS.secondary }}>{pageData.hero_subtitle}</span>
                </h1>

                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  {pageData.hero_description}
                </p>

                <div className="flex flex-wrap gap-6 mb-8 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Clock size={18} style={{ color: COLORS.secondary }} />
                    <span>{pageData.batch_duration} Duration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={18} style={{ color: COLORS.secondary }} />
                    <span>Live Online Sessions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award size={18} style={{ color: COLORS.secondary }} />
                    <span>Certificate Included</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    data-testid="hero-enroll-btn"
                    className="px-8 py-4 text-white rounded-lg font-semibold hover:opacity-90 transition-all flex items-center justify-center gap-2"
                    style={{ background: COLORS.gradient }}
                  >
                    Request Enrollment
                    <ArrowRight size={18} />
                  </button>
                  <a
                    href="#curriculum"
                    className="px-8 py-4 border-2 rounded-lg font-medium hover:bg-slate-50 transition-all text-center"
                    style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                  >
                    View Curriculum
                  </a>
                </div>
              </div>

              {/* Image */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={pageData.hero_image}
                    alt="Professional Training Session"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Stats Card */}
                {pageData.stats && pageData.stats.length >= 2 && (
                  <div className="absolute -bottom-6 -left-6 sm:left-6 bg-white rounded-xl shadow-xl p-5 border border-slate-100">
                    <div className="flex gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold" style={{ color: COLORS.primary }}>{pageData.stats[0].value}</div>
                        <div className="text-xs text-slate-500">{pageData.stats[0].label.split(' ')[0]}</div>
                      </div>
                      <div className="text-center border-l border-slate-200 pl-6">
                        <div className="text-2xl font-bold" style={{ color: COLORS.secondary }}>{pageData.stats[1].value}</div>
                        <div className="text-xs text-slate-500">{pageData.stats[1].label.split(' ')[0]}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        {pageData.stats && pageData.stats.length > 0 && (
          <section className="py-12" style={{ backgroundColor: COLORS.dark }}>
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {pageData.stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-slate-300 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Modules Section */}
        {pageData.modules && pageData.modules.length > 0 && (
          <section id="curriculum" className="py-20 bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-14">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: COLORS.dark }}>
                  {pageData.modules_title}
                </h2>
                {pageData.modules_subtitle && (
                  <p className="text-slate-600 max-w-2xl mx-auto">
                    {pageData.modules_subtitle}
                  </p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {pageData.modules.map((module, index) => {
                  const Icon = getIcon(module.icon);
                  return (
                    <div
                      key={index}
                      className="p-6 bg-slate-50 rounded-xl hover:shadow-lg transition-all group border border-transparent hover:border-slate-200"
                    >
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform"
                        style={{ background: COLORS.gradient }}
                      >
                        <Icon className="text-white" size={24} />
                      </div>
                      <h3 className="text-lg font-semibold mb-2" style={{ color: COLORS.dark }}>{module.title}</h3>
                      <p className="text-slate-600 text-sm">{module.description}</p>
                    </div>
                  );
                })}
              </div>

              <div className="mt-12 text-center">
                <button
                  onClick={() => setIsModalOpen(true)}
                  data-testid="syllabus-btn"
                  className="px-8 py-4 text-white rounded-lg font-semibold hover:opacity-90 transition-all inline-flex items-center gap-2"
                  style={{ background: COLORS.gradient }}
                >
                  Get Detailed Syllabus
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Why Choose Us */}
        {pageData.benefits && pageData.benefits.length > 0 && (
          <section className="py-20 bg-slate-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ color: COLORS.dark }}>
                    {pageData.benefits_title}
                  </h2>
                  <div className="space-y-6">
                    {pageData.benefits.map((item, index) => {
                      const Icon = getIcon(item.icon);
                      return (
                        <div key={index} className="flex gap-4">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                            style={{ background: COLORS.gradient }}
                          >
                            <Icon className="text-white" size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold mb-1" style={{ color: COLORS.dark }}>{item.title}</h3>
                            <p className="text-slate-600 text-sm">{item.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="relative">
                  <img
                    src={pageData.benefits_image}
                    alt="Successful Team"
                    className="rounded-xl shadow-lg w-full"
                  />
                  {pageData.stats && pageData.stats.length >= 3 && (
                    <div 
                      className="absolute -bottom-4 -right-4 rounded-xl p-4 shadow-xl text-white"
                      style={{ background: COLORS.gradient }}
                    >
                      <div className="text-2xl font-bold">{pageData.stats[2].value}</div>
                      <div className="text-sm opacity-90">{pageData.stats[2].label}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Testimonials */}
        {pageData.testimonials && pageData.testimonials.length > 0 && (
          <section className="py-20 bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-14">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: COLORS.dark }}>
                  {pageData.testimonials_title}
                </h2>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {pageData.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} size={16} style={{ color: '#F59E0B', fill: '#F59E0B' }} />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 text-sm leading-relaxed">"{testimonial.quote}"</p>
                    <div>
                      <div className="font-semibold" style={{ color: COLORS.dark }}>{testimonial.name}</div>
                      <div className="text-sm" style={{ color: COLORS.secondary }}>{testimonial.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        {pageData.faqs && pageData.faqs.length > 0 && (
          <section className="py-20 bg-slate-50">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
              <div className="text-center mb-12">
                <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: COLORS.dark }}>
                  {pageData.faqs_title}
                </h2>
              </div>
              <div className="bg-white rounded-xl p-6 sm:p-8 shadow-sm">
                {pageData.faqs.map((faq, index) => (
                  <FAQItem
                    key={index}
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openFAQ === index}
                    onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-20" style={{ background: COLORS.gradient }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {pageData.cta_title}
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              {pageData.cta_description}
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              data-testid="cta-enroll-btn"
              className="px-10 py-4 bg-white rounded-lg font-semibold hover:bg-slate-100 transition-all inline-flex items-center gap-2"
              style={{ color: COLORS.primary }}
            >
              {pageData.cta_button_text}
              <ArrowRight size={18} />
            </button>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-4 bg-slate-100 border-t border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-start gap-3 text-xs text-slate-500">
              <AlertTriangle size={14} className="text-slate-400 shrink-0 mt-0.5" />
              <p>
                <strong>Disclaimer:</strong> SKANDA IT SOLUTIONS is an independent training provider. We are not authorized partners, affiliates, or official representatives of SAP SE, Meta Platforms Inc., Facebook, Instagram, or any of their subsidiaries. All trademarks belong to their respective owners.
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-10 border-t border-slate-200" style={{ backgroundColor: COLORS.dark }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Skanda IT Solutions" className="h-8 w-auto bg-white rounded p-1" />
                <span className="text-white font-medium">SKANDA IT SOLUTIONS</span>
              </div>
              <a
                href="mailto:info@skandaitsolutions.com"
                className="text-slate-300 hover:text-white transition-colors flex items-center gap-2 text-sm"
              >
                <Mail size={16} />
                info@skandaitsolutions.com
              </a>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-600 text-center text-slate-400 text-xs">
              © 2026 SKANDA IT SOLUTIONS. All rights reserved.
            </div>
          </div>
        </footer>
      </div>

      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleRegistrationSuccess}
      />
    </>
  );
}
